import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { formatGb, resourceUsagePercent } from '../overrides';

/**
 * Override-container memory usage bar for the Tenants page header (PTV2-1021
 * Phase 2). Rendered only when the deployment manager is reachable. Tints red
 * above 80% so a dev knows to stop an override tenant before deploying another.
 */
export function ResourceUsageBar({
  usedBytes,
  ceilingBytes,
}: {
  readonly usedBytes: number;
  readonly ceilingBytes: number;
}): ReactElement {
  const { t } = useTranslation();
  const pct = resourceUsagePercent(usedBytes, ceilingBytes);
  const high = pct >= 80;

  return (
    <div className="flex items-center gap-2" aria-label={t('routes.platform.tenants.resourceBar.ariaLabel')}>
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {t('routes.platform.tenants.resourceBar.label')}
      </span>
      <div
        className="h-1.5 w-28 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
      >
        <div
          className={cn('h-full rounded-full transition-[width]', high ? 'bg-destructive' : 'bg-primary')}
          style={{ width: `${String(pct)}%` }}
        />
      </div>
      <span className={cn('text-[12px] tabular-nums', high ? 'text-destructive' : 'text-muted-foreground')}>
        {t('routes.platform.tenants.resourceBar.usage', {
          used: formatGb(usedBytes),
          ceiling: formatGb(ceilingBytes),
        })}
      </span>
    </div>
  );
}
